/**
 * 基准点管理器。
 * 负责创建、管理所有正式基准点对象。
 */

import * as THREE from 'three';
import { DatumPoint, type DatumPointOptions } from '../reference/DatumPoint';
import { ReferenceCreationContext } from '../reference/ReferenceCreationContext';
import {
  DATUM_POINT_HISTORY_ENTITY_ID,
  type DatumPointHistoryRecord,
  type DatumPointHistorySnapshot,
  type DatumPointHistoryVector3,
  type UserCreatedDatumPointTimelineEntry,
} from '../reference/DatumPointHistoryTypes';
import type { Viewer } from '../viewer/Viewer';
import { addObject3DToParent } from '../utils/scene-object/addObject3DToParent';

//------ 基准点JSON序列化 开始------
/**
 * 基准点 JSON 协议版本。
 */
export enum DatumPointJSONSchemaVersion {
  /** 初始版本：保存 DatumPointManager 可恢复的正式基准点 plain data。 */
  V1 = 1,
}

/**
 * 基准点管理器 JSON 文档。
 *
 * 设计说明：
 * 1. 该结构复用历史快照 plain data，避免 Manager 内维护第二套并行持久化模型；
 * 2. 默认原点仍由 Manager 构造阶段创建，不进入 JSON；
 * 3. 当前不接入离线包导入导出，只供上层后续统一编排。
 */
export interface DatumPointDocumentJSON extends DatumPointHistorySnapshot {
  /** JSON 协议版本。 */
  readonly schemaVersion: DatumPointJSONSchemaVersion.V1;
}
//------ 基准点JSON序列化 结束------

/**
 * 基准点管理器。
 */
export class DatumPointManager {
  /** 基准点根组名称。 */
  private static readonly ROOT_GROUP_NAME = 'DatumPointGroup';
  /** 基准点标签根组名称。 */
  private static readonly LABEL_GROUP_NAME = 'DatumPointLabelGroup';
  /** Viewer 实例。 */
  private readonly viewer: Viewer;
  /** 场景引用。 */
  private readonly scene: THREE.Scene;
  /** 基准点根组。 */
  private readonly rootGroup: THREE.Group;
  /** 基准点标签根组。 */
  private readonly labelGroup: THREE.Group;
  /** 基准点集合。 */
  private readonly points: Map<number, DatumPoint> = new Map();
  /** 用户创建基准点名称前缀。 */
  private static readonly CUSTOM_POINT_NAME_PREFIX = '基准点';
  /**
   * 用户手动创建基准点的设计树时间线登记数组。
   *
   * 设计说明：
   * 1. 与 `points` Map 解耦，专门服务设计树按创建时间排序；
   * 2. 默认原点不会写入该数组；
   * 3. Undo/Redo 恢复时通过历史记录中的 `createdAt` 重建该数组。
   */
  private readonly userCreatedPointTimeline: UserCreatedDatumPointTimelineEntry[] = [];

  /**
   * @param viewer Viewer 实例。
   * @param options 配置参数。
   */
  public constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.scene = viewer.scene;
    this.rootGroup = new THREE.Group();
    this.rootGroup.name = DatumPointManager.ROOT_GROUP_NAME;
    this.labelGroup = new THREE.Group();
    this.labelGroup.name = DatumPointManager.LABEL_GROUP_NAME;
    addObject3DToParent(this.rootGroup, this.scene);
    addObject3DToParent(this.labelGroup, this.scene);
    //------ 默认原点基准点 开始------
    this.createDefaultOriginPoint();
    //------ 默认原点基准点 结束------
  }

  /**
   * 创建基准点。
   *
   * @param options 构造参数。
   * @returns 基准点对象。
   */
  public createPoint(options: DatumPointOptions): DatumPoint {
    //------ 基准点显式命名保留 开始------
    const normalizedName = options.name.trim();
    const point = new DatumPoint({
      ...options,
      name: normalizedName.length > 0 ? normalizedName : this.generateNextCustomPointName(),
    });
    //------ 基准点显式命名保留 结束------
    this.points.set(point.datumPointId, point);
    addObject3DToParent(point, this.rootGroup);
    const labelObject = point.getLabelObject();
    if (labelObject) {
      addObject3DToParent(labelObject, this.labelGroup);
    }
    if (
      !point.isOrigin
      && point.creationContext !== ReferenceCreationContext.DefaultBuiltIn
    ) {
      this.registerUserCreatedPointTimelineEntry(point.datumPointId, options.createdAt);
    }
    return point;
  }

  /**
   * 删除基准点。
   * 原点基准点（isOrigin = true）禁止删除。
   *
   * @param pointId 基准点 ID。
   */
  public removePoint(pointId: number): void {
    const point = this.points.get(pointId);
    if (!point) {
      return;
    }
    // 原点基准点禁止删除
    if (point.isOrigin) {
      console.warn('无法删除原点基准点，原点是系统默认基准对象');
      return;
    }
    this.rootGroup.remove(point);
    const labelObject = point.getLabelObject();
    if (labelObject) {
      this.labelGroup.remove(labelObject);
    }
    point.dispose();
    this.points.delete(pointId);
    this.unregisterUserCreatedPointTimelineEntry(pointId);
  }

  /**
   * 读取用户手动创建基准点的创建时间戳。
   *
   * @param pointId 基准点稳定业务 ID。
   * @returns 创建时间戳（毫秒）；非用户创建基准点时返回 `undefined`。
   */
  public getUserCreatedPointCreatedAt(pointId: number): number | undefined {
    return this.userCreatedPointTimeline.find((entry) => entry.pointId === pointId)?.createdAt;
  }

  /**
   * 登记用户手动创建基准点的设计树时间线项。
   *
   * @param pointId 基准点稳定业务 ID。
   * @param createdAt 创建时间戳；缺省时写入当前时间。
   */
  private registerUserCreatedPointTimelineEntry(pointId: number, createdAt?: number): void {
    const existingEntry = this.userCreatedPointTimeline.find((entry) => entry.pointId === pointId);
    if (existingEntry) {
      return;
    }
    this.userCreatedPointTimeline.push({
      pointId,
      createdAt: Number.isFinite(createdAt) ? Number(createdAt) : Date.now(),
    });
  }

  /**
   * 移除用户手动创建基准点的设计树时间线项。
   *
   * @param pointId 基准点稳定业务 ID。
   */
  private unregisterUserCreatedPointTimelineEntry(pointId: number): void {
    const entryIndex = this.userCreatedPointTimeline.findIndex((entry) => entry.pointId === pointId);
    if (entryIndex >= 0) {
      this.userCreatedPointTimeline.splice(entryIndex, 1);
    }
  }

  /**
   * 获取基准点。
   *
   * @param pointId 基准点 ID。
   * @returns 基准点对象；未命中时返回 `undefined`。
   */
  public getPoint(pointId: number): DatumPoint | undefined {
    return this.points.get(pointId);
  }

  /**
   * 重命名基准点。
   *
   * @param pointId 基准点 ID。
   * @param nextName 新名称。
   * @returns 是否重命名成功。
   */
  public renamePoint(pointId: number, nextName: string): boolean {
    const point = this.points.get(pointId);
    const normalizedName = nextName.trim();
    if (!point || normalizedName.length === 0) {
      return false;
    }
    point.setName(normalizedName);
    this.viewer.requestRender();
    return true;
  }

  /**
   * 获取全部基准点。
   *
   * @returns 基准点集合。
   */
  public getAllPoints(): DatumPoint[] {
    return Array.from(this.points.values());
  }

  /**
   * 设置基准点可见性。
   *
   * @param pointId 基准点 ID。
   * @param visible 是否可见。
   */
  public setPointVisible(pointId: number, visible: boolean): void {
    this.points.get(pointId)?.setVisible(visible);
  }

  /**
   * 隐藏全部基准点。
   */
  public hideAllPoints(): void {
    this.points.forEach((point) => {
      point.setVisible(false);
    });
  }

  //------ 基准点JSON序列化 开始------
  /**
   * 导出当前基准点管理器 JSON。
   *
   * 设计说明：
   * 1. 默认原点是系统内置对象，不进入 JSON，恢复时继续由构造函数创建；
   * 2. 返回值只包含 plain data，不包含 Three.js Sprite、材质、标签或场景引用；
   * 3. 当前 API 不写入 `.puppy`，只提供 Manager 级序列化能力。
   *
   * @returns 可持久化的基准点 JSON 文档。
   */
  public toJSON(): DatumPointDocumentJSON {
    const snapshot = this.createHistorySnapshot();

    /**
     * 通过 JSON 克隆主动移除 `undefined` 与运行时原型，保证调用方拿到的就是可写盘数据。
     */
    return this.cloneDatumPointDocumentJSON({
      schemaVersion: DatumPointJSONSchemaVersion.V1,
      ...snapshot,
    });
  }

  /**
   * 从 JSON 恢复基准点管理器状态。
   *
   * 设计说明：
   * 1. 先校验协议版本与必要字段，避免无效 JSON 进入对象重建流程；
   * 2. 复用 `restoreHistorySnapshot()` 完成删除旧用户基准点、重建对象、清理选择和刷新渲染；
   * 3. 当前只恢复 Manager 掌握的正式点对象，不恢复 `ReferencePointController` 中的可复算特征记录。
   *
   * @param data 外部传入的基准点 JSON。
   */
  public fromJSON(data: unknown): void {
    this.assertDatumPointDocumentJSON(data);
    const document = this.cloneDatumPointDocumentJSON(data);

    /**
     * Manager 的恢复入口只消费历史快照字段，`schemaVersion` 在校验后不再传入。
     */
    this.restoreHistorySnapshot({
      managerId: document.managerId,
      points: document.points,
      features: document.features,
    });
  }

  /**
   * 克隆基准点 JSON 文档。
   *
   * @param document 来源 JSON 文档。
   * @returns 去除运行时引用后的 JSON 文档。
   */
  private cloneDatumPointDocumentJSON(document: DatumPointDocumentJSON): DatumPointDocumentJSON {
    return JSON.parse(JSON.stringify(document)) as DatumPointDocumentJSON;
  }

  /**
   * 断言未知值是基准点 JSON 文档。
   *
   * @param data 外部输入。
   */
  private assertDatumPointDocumentJSON(data: unknown): asserts data is DatumPointDocumentJSON {
    if (!this.isRecord(data)) {
      throw new Error('基准点 JSON 必须是对象。');
    }
    if (data.schemaVersion !== DatumPointJSONSchemaVersion.V1) {
      throw new Error(`不支持的基准点 JSON 版本：${String(data.schemaVersion)}`);
    }
    if (data.managerId !== DATUM_POINT_HISTORY_ENTITY_ID) {
      throw new Error(`基准点 JSON.managerId 不匹配：${String(data.managerId)}`);
    }
    if (!Array.isArray(data.points)) {
      throw new Error('基准点 JSON.points 必须是数组。');
    }
    if (!Array.isArray(data.features)) {
      throw new Error('基准点 JSON.features 必须是数组。');
    }

    /**
     * 正式点对象恢复依赖稳定 ID 与世界坐标，因此这里逐条校验，避免恢复中途才出现难定位错误。
     */
    const pointIds = new Set<number>();
    data.points.forEach((record, index) => {
      this.assertDatumPointHistoryRecord(record, index);
      if (pointIds.has(record.pointId)) {
        throw new Error(`基准点 JSON.points[${index}].pointId 重复：${record.pointId}`);
      }
      pointIds.add(record.pointId);
    });
  }

  /**
   * 断言未知值是单条基准点历史记录。
   *
   * @param value 外部输入。
   * @param index 数组下标。
   */
  private assertDatumPointHistoryRecord(
    value: unknown,
    index: number,
  ): asserts value is DatumPointHistoryRecord {
    if (!this.isRecord(value)) {
      throw new Error(`基准点 JSON.points[${index}] 必须是对象。`);
    }
    if (!Number.isFinite(value.pointId)) {
      throw new Error(`基准点 JSON.points[${index}].pointId 必须是有限数字。`);
    }
    if (typeof value.name !== 'string' || value.name.trim().length === 0) {
      throw new Error(`基准点 JSON.points[${index}].name 必须是非空字符串。`);
    }
    if (!this.isReferenceCreationContext(value.creationContext)) {
      throw new Error(`基准点 JSON.points[${index}].creationContext 不合法。`);
    }
    if (value.creationContext === ReferenceCreationContext.DefaultBuiltIn) {
      throw new Error(`基准点 JSON.points[${index}].creationContext 不允许恢复默认内置对象。`);
    }

    this.assertDatumPointHistoryVector3(value.position, `基准点 JSON.points[${index}].position`);

    if (typeof value.visible !== 'boolean') {
      throw new Error(`基准点 JSON.points[${index}].visible 必须是布尔值。`);
    }
    if (typeof value.isOrigin !== 'boolean') {
      throw new Error(`基准点 JSON.points[${index}].isOrigin 必须是布尔值。`);
    }
    if (value.isOrigin) {
      throw new Error(`基准点 JSON.points[${index}] 不允许恢复默认原点。`);
    }
    if (
      value.createdAt !== undefined
      && !Number.isFinite(value.createdAt)
    ) {
      throw new Error(`基准点 JSON.points[${index}].createdAt 必须是有限数字。`);
    }
  }

  /**
   * 断言未知值是基准点历史三维向量。
   *
   * @param value 外部输入。
   * @param fieldName 字段名。
   */
  private assertDatumPointHistoryVector3(
    value: unknown,
    fieldName: string,
  ): asserts value is DatumPointHistoryVector3 {
    if (
      !Array.isArray(value)
      || value.length !== 3
      || value.some((item) => !Number.isFinite(item))
    ) {
      throw new Error(`${fieldName} 必须是三个有限数字。`);
    }
  }

  /**
   * 判断未知值是否为普通记录对象。
   *
   * @param value 待判断值。
   * @returns 是否为普通记录对象。
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
  }

  /**
   * 判断未知值是否为合法创建来源。
   *
   * @param value 待判断值。
   * @returns 是否为合法创建来源。
   */
  private isReferenceCreationContext(value: unknown): value is ReferenceCreationContext {
    return Object.values(ReferenceCreationContext).includes(value as ReferenceCreationContext);
  }
  //------ 基准点JSON序列化 结束------

  //------ 基准点撤销重做 开始------
  /**
   * 捕获用户创建基准点的历史快照。
   *
   * 设计说明：
   * 1. 默认原点是系统内置对象，不进入模型历史；
   * 2. 用户创建的零件态和装配体态基准点需要保存数值 ID，保证 Redo 后引用 ID 不变化；
   * 3. 快照只保存 plain data，不保存 THREE.Object3D、材质或标签对象。
   *
   * @returns 基准点历史快照。
   */
  public createHistorySnapshot(): DatumPointHistorySnapshot {
    return {
      managerId: DATUM_POINT_HISTORY_ENTITY_ID,
      points: this.getAllPoints()
        .filter((point) => point.creationContext !== ReferenceCreationContext.DefaultBuiltIn && !point.isOrigin)
        .map((point) => this.createHistoryRecordFromPoint(point)),
      features: [],
    };
  }

  /**
   * 按历史快照恢复用户创建的基准点。
   *
   * @param snapshot 基准点历史快照。
   */
  public restoreHistorySnapshot(snapshot: DatumPointHistorySnapshot): void {
    this.getAllPoints()
      .filter((point) => point.creationContext !== ReferenceCreationContext.DefaultBuiltIn && !point.isOrigin)
      .forEach((point) => {
        this.removePoint(point.datumPointId);
      });
    this.userCreatedPointTimeline.length = 0;

    (snapshot.points ?? [])
      .filter((record) => !record.isOrigin)
      .forEach((record, index) => {
        this.createPoint({
          id: record.pointId,
          creationContext: record.creationContext,
          name: record.name,
          position: this.createVectorFromHistory(record.position),
          visible: record.visible,
          isOrigin: false,
          createdAt: Number.isFinite(record.createdAt)
            ? Number(record.createdAt)
            : Date.now() + index,
        });
      });

    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.requestRender();
  }

  /**
   * 从基准点创建历史记录。
   *
   * @param point 基准点对象。
   * @returns 基准点历史记录。
   */
  private createHistoryRecordFromPoint(point: DatumPoint): DatumPointHistoryRecord {
    return {
      pointId: point.datumPointId,
      name: point.name,
      creationContext: point.creationContext,
      position: this.createHistoryVector(point.getWorldPoint()),
      visible: point.isVisible(),
      isOrigin: point.isOrigin,
      createdAt: this.getUserCreatedPointCreatedAt(point.datumPointId),
    };
  }

  /**
   * 创建历史向量。
   *
   * @param vector Three.js 向量。
   * @returns plain data 向量。
   */
  private createHistoryVector(vector: THREE.Vector3): DatumPointHistoryVector3 {
    return [vector.x, vector.y, vector.z];
  }

  /**
   * 从历史向量创建 Three.js 向量。
   *
   * @param vector 历史向量。
   * @returns Three.js 向量。
   */
  private createVectorFromHistory(vector: DatumPointHistoryVector3): THREE.Vector3 {
    return new THREE.Vector3(vector[0], vector[1], vector[2]);
  }
  //------ 基准点撤销重做 结束------

  /**
   * 释放全部资源。
   */
  public dispose(): void {
    this.getAllPoints().forEach((point) => {
      this.rootGroup.remove(point);
      const labelObject = point.getLabelObject();
      if (labelObject) {
        this.labelGroup.remove(labelObject);
      }
      point.dispose();
    });
    this.points.clear();
    this.userCreatedPointTimeline.length = 0;
    this.scene.remove(this.rootGroup);
    this.scene.remove(this.labelGroup);
  }

  /**
   * 生成下一个用户创建基准点名称。
   *
   * @returns 形如“基准点1”的名称。
   */
  private generateNextCustomPointName(): string {
    const prefix = DatumPointManager.CUSTOM_POINT_NAME_PREFIX;
    let nextIndex = 1;
    const usedNames = new Set(this.getAllPoints().map((point) => point.name));
    while (usedNames.has(`${prefix}${nextIndex}`)) {
      nextIndex += 1;
    }
    return `${prefix}${nextIndex}`;
  }

  //------ 默认原点基准点 开始------
  /**
   * 创建默认原点基准点。
   *
   * 设计说明：
   * 1. 原点属于基准点管理域内的默认参考对象，应由管理器在构造阶段统一落地；
   * 2. 固定命名为“原点”，固定坐标为 (0,0,0)；
   * 3. 通过统一创建入口落地，避免出现绕过管理器导致的数据不一致。
   */
  private createDefaultOriginPoint(): void {
    this.createPoint({
      creationContext: ReferenceCreationContext.DefaultBuiltIn,
      name: '原点',
      position: new THREE.Vector3(0, 0, 0),
      isOrigin: true,
    });
  }
  //------ 默认原点基准点 结束------
}
